import React, { useState, useMemo, useEffect } from "react";
import { CheckCircle2, XCircle, FileText, Users, User, HelpCircle, Mail, Search, ChevronDown, Bell } from "lucide-react";

// ─── FAQ Data ─────────────────────────────────────────────────────────────
const FAQ_CATEGORIES = ["Все", "Общие вопросы", "Безопасность", "Для заказчиков", "Для исполнителей"];

const FAQ_ITEMS = [
  {
    category: "Общие вопросы",
    q: "Что такое Реверсия?",
    a: "Реверсия — российская биржа поручений на местах. Заказчики из любого города публикуют поручения, которые нужно выполнить физически в конкретном месте: сфотографировать объект, провести проверку тайного покупателя, заказать мемориальный сервис (уход за могилой). Местные исполнители берут поручения и загружают результат. Оплата проходит безопасно через систему эскроу."
  },
  {
    category: "Общие вопросы",
    q: "В каких городах работает Реверсия?",
    a: "Заказчик может находиться в любом городе России или за рубежом. Исполнение заданий сейчас — в Краснодаре и Краснодарском крае: здесь работает база проверенных исполнителей. Если задание нужно выполнить в другом городе, его можно разместить, но отклик зависит от наличия исполнителей рядом."
  },
  {
    category: "Общие вопросы",
    q: "Как быстро можно найти исполнителя?",
    a: "Скорость отклика зависит от типа задания, района и цены. Сейчас база исполнителей Реверсии — Краснодар и Краснодарский край, поэтому задания здесь находят исполнителя быстрее всего. Заказчик сам выбирает исполнителя из откликнувшихся по рейтингу и стоимости."
  },
  {
    category: "Безопасность",
    q: "Как работает безопасная оплата на Реверсии?",
    a: "Реверсия использует систему эскроу: заказчик вносит оплату при создании задания, деньги замораживаются на платформе и не поступают исполнителю до тех пор, пока заказчик не примет результат. Это защищает обе стороны: исполнитель гарантированно получит деньги за выполненную работу, а заказчик — только за качественный результат."
  },
  {
    category: "Безопасность",
    q: "Как долго деньги находятся в эскроу?",
    a: "Деньги хранятся до подтверждения выполнения задания заказчиком. Максимальный срок — 7 дней с момента загрузки результатов исполнителем. По истечении этого срока задание считается выполненным автоматически."
  },
  {
    category: "Безопасность",
    q: "Что делать, если исполнитель выполнил задание некачественно?",
    a: "На Реверсии есть система защиты заказчика. Порядок действий такой: сначала заказчик может один раз вернуть задание исполнителю на доработку напрямую (кнопка «Вернуть на доработку»), конкретно указав, что нужно исправить — у исполнителя будет 48 часов. Если после доработки результат снова не устраивает, заказчик открывает спор — его рассмотрит администрация и примет решение. Деньги из эскроу не поступят исполнителю до разрешения спора. Каждый исполнитель имеет рейтинг и отзывы, которые помогают выбрать надёжного человека."
  },
  {
    category: "Безопасность",
    q: "Сколько раз можно вернуть задание на доработку?",
    a: "Напрямую, без участия администратора — один раз: в момент проверки результата нажмите «Вернуть на доработку» и опишите, что нужно исправить. Все последующие возвраты возможны только через спор: администратор рассмотрит ситуацию и либо повторно направит работу исполнителю на доработку, либо примет финальное решение в пользу одной из сторон."
  },
  {
    category: "Безопасность",
    q: "Как работает система споров?",
    a: "При возникновении спора обе стороны могут представить доказательства. Администрация рассматривает спор в течение 24–48 часов и выносит решение. Деньги из эскроу не поступают исполнителю до окончания рассмотрения спора."
  },
  {
    category: "Для заказчиков",
    q: "Какие задания можно разместить на Реверсии?",
    a: "На Реверсии можно разместить задания пяти основных типов: фото и видеофиксация объекта или события; проверка тайного покупателя в магазине, кафе или офисе; аудит объекта недвижимости или торговой точки; мемориальный сервис — уход за могилой (уборка, цветы, фотоотчёт); материал для мобильной журналистики (репортажи, интервью, съёмка с места событий)."
  },
  {
    category: "Для заказчиков",
    q: "Как заказать тайного покупателя через Реверсию?",
    a: "Зарегистрируйтесь как заказчик, создайте задание типа «Тайный покупатель», укажите адрес проверяемой точки, критерии оценки, желаемые сроки и бюджет. Местный исполнитель возьмёт задание, посетит заведение под видом обычного клиента и предоставит подробный отчёт с фото или видеофиксацией."
  },
  {
    category: "Для заказчиков",
    q: "Можно ли заказать уход за могилой в Краснодаре, если я живу в другом городе?",
    a: "Да, для этого на Реверсии есть мемориальный сервис. Если ваши близкие похоронены в Краснодаре или крае, а вы живёте в другом городе, разместите задание с адресом захоронения. Проверенный исполнитель приедет на кладбище, наведёт порядок, возложит цветы и пришлёт фотоотчёт. Оплата резервируется на задании и переводится исполнителю только после того, как вы примете результат."
  },
  {
    category: "Для заказчиков",
    q: "Сколько стоит разместить задание на Реверсии?",
    a: "Размещение задания бесплатное. Заказчик сам указывает бюджет, который готов заплатить исполнителю. Платформа берёт небольшую комиссию с успешно выполненных заданий. Минимальная стоимость задания зависит от его типа и сложности."
  },
  {
    category: "Для заказчиков",
    q: "Можно ли отменить задание?",
    a: "Да, до момента принятия задания исполнителем — отмена бесплатная. После принятия отмена возможна только через систему споров с обоснованием причины. При отмене до принятия деньги возвращаются на счёт заказчика."
  },
  {
    category: "Для заказчиков",
    q: "Где найти кнопку оплаты доплаты?",
    a: "После того как исполнитель подтвердит новую цену, на странице детального просмотра задания появится блок «Требуется доплата» с кнопкой оплаты. Также в разделе «Обсуждения» рядом с заданием будет оранжевый индикатор с суммой доплаты."
  },
  {
    category: "Для исполнителей",
    q: "Кто может стать исполнителем на Реверсии?",
    a: "Стать исполнителем может любой житель России с смартфоном и желанием зарабатывать. Регистрация бесплатная. Исполнители сами выбирают задания в своём городе и устанавливают свою стоимость. Специального образования не требуется — достаточно честно выполнять задания и поддерживать высокий рейтинг."
  },
  {
    category: "Для исполнителей",
    q: "Какая комиссия платформы для исполнителей?",
    a: "Комиссия составляет от 10% до 25% в зависимости от типа задания. Заказчик оплачивает полную сумму, комиссия вычитается из выплаты исполнителю. Точный процент отображается при просмотре каждого задания."
  },
  {
    category: "Для исполнителей",
    q: "Почему не отображается кнопка «Принять задание»?",
    a: "Если вы согласились на новую цену, но кнопка не отображается — это означает, что заказчик ещё не оплатил доплату. Вы увидите сообщение «Ожидаем оплату доплаты заказчиком». Кнопка появится автоматически после оплаты."
  },
  {
    category: "Для исполнителей",
    q: "Почему ZIP-архив пустой при скачивании?",
    a: "Попробуйте скачать файлы по отдельности — нажмите кнопку «Скачать» под каждым файлом. Если проблема повторяется, обратитесь в поддержку: support@reversia.ru"
  },
];

// ─── FAQ Accordion Section ──────────────────────────────────────────────────
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setFaqCategory] = useState("Все");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    return FAQ_ITEMS.filter((item) => {
      const matchesCategory = activeCategory === "Все" || item.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleToggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <div style={{ display: 'block', overflowX: 'hidden' }}>
      {/* Section header */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-navy)' }}>
          Частые вопросы
        </h2>
        <p className="text-sm" style={{ color: 'var(--rv-text-muted)', fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Ответы на самые распространённые вопросы о платформе
        </p>
      </div>

      {/* Search — always full width on top */}
      <div className="relative mb-4">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: 'var(--rv-text-muted)' }}
        />
        <input
          data-testid="faq-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setOpenIndex(null); }}
          placeholder="Поиск по вопросам..."
          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border focus:outline-none transition-colors"
          style={{
            border: '1px solid var(--rv-border)',
            background: 'white',
            color: 'var(--rv-text-primary)',
            fontFamily: 'IBM Plex Sans, sans-serif',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--rv-teal)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,148,136,0.1)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--rv-border)'; e.target.style.boxShadow = 'none'; }}
        />
      </div>

      {/* Mobile: horizontal scrollable category chips */}
      <div className="flex lg:hidden gap-2 overflow-x-auto pb-2 mb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {FAQ_CATEGORIES.map((cat) => (
          <button
            key={cat}
            data-testid={`faq-category-btn-${cat}`}
            onClick={() => { setFaqCategory(cat); setOpenIndex(null); }}
            className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-150 whitespace-nowrap"
            style={{
              fontFamily: 'IBM Plex Sans, sans-serif',
              background: activeCategory === cat ? 'var(--rv-teal)' : 'white',
              color: activeCategory === cat ? 'white' : 'var(--rv-text-muted)',
              border: activeCategory === cat ? '1px solid var(--rv-teal)' : '1px solid var(--rv-border)',
              cursor: 'pointer',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Content: two-column on desktop, single column on mobile */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">

        {/* Desktop only: left sidebar with vertical category list */}
        <div className="hidden lg:block w-52 flex-shrink-0 sticky top-4">
          <nav className="space-y-1">
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`faq-category-btn-desktop-${cat}`}
                onClick={() => { setFaqCategory(cat); setOpenIndex(null); }}
                className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  background: activeCategory === cat ? 'rgba(13,148,136,0.1)' : 'transparent',
                  color: activeCategory === cat ? 'var(--rv-teal)' : 'var(--rv-text-muted)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {cat}
              </button>
            ))}
          </nav>
        </div>

        {/* Accordion — always visible on all screen sizes */}
        <div className="flex-1 min-w-0 w-full">
          {filtered.length === 0 ? (
            <div
              className="reversia-card p-8 text-center"
              style={{ color: 'var(--rv-text-muted)', fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <HelpCircle size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">По вашему запросу ничего не найдено.</p>
              <button
                className="mt-3 text-sm underline"
                style={{ color: 'var(--rv-teal)', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => { setSearchQuery(''); setFaqCategory('Все'); }}
              >
                Сбросить фильтры
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <div
                    key={idx}
                    data-testid={`faq-item-${idx}`}
                    className="reversia-card overflow-hidden transition-all duration-200"
                    style={{
                      boxShadow: isOpen ? '0 4px 16px rgba(13,148,136,0.10)' : undefined,
                      borderColor: isOpen ? 'rgba(13,148,136,0.3)' : undefined,
                    }}
                  >
                    <button
                      data-testid={`faq-trigger-${idx}`}
                      onClick={() => handleToggle(idx)}
                      className="w-full flex items-center justify-between gap-3 p-4 lg:p-5 text-left"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <span
                        className="text-sm font-semibold leading-snug"
                        style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-text-primary)' }}
                      >
                        {item.q}
                      </span>
                      <ChevronDown
                        size={18}
                        className="flex-shrink-0 transition-transform duration-300"
                        style={{
                          color: 'var(--rv-text-muted)',
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>

                    {/* CSS Grid animated answer */}
                    <div
                      className="transition-all duration-300 ease-in-out"
                      style={{
                        display: 'grid',
                        gridTemplateRows: isOpen ? '1fr' : '0fr',
                        opacity: isOpen ? 1 : 0,
                      }}
                    >
                      <div style={{ overflow: 'hidden' }}>
                        <div
                          className="px-4 lg:px-5 pb-4 lg:pb-5 text-sm leading-relaxed"
                          style={{
                            color: 'var(--rv-text-muted)',
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            borderTop: '1px solid var(--rv-border)',
                            paddingTop: '1rem',
                          }}
                        >
                          {item.a}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length > 0 && (
            <p className="mt-4 text-xs" style={{ color: 'var(--rv-text-muted)', fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Показано {filtered.length} {filtered.length === 1 ? 'вопрос' : filtered.length < 5 ? 'вопроса' : 'вопросов'}
              {activeCategory !== 'Все' && ` в категории «${activeCategory}»`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
export const HelpAndFAQ = ({ setCurrentView }) => {
  const [activeCategory, setActiveCategory] = useState('customer');

  useEffect(() => {
    const initTab = localStorage.getItem('helpInitTab');
    const scrollTo = localStorage.getItem('helpScrollTo');
    if (initTab) {
      setActiveCategory(initTab);
      localStorage.removeItem('helpInitTab');
    }
    if (scrollTo) {
      localStorage.removeItem('helpScrollTo');
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${scrollTo}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, []);

  const tabs = [
    { id: 'customer', label: 'Для заказчиков', Icon: Users },
    { id: 'executor', label: 'Для исполнителей', Icon: User },
    { id: 'faq', label: 'Частые вопросы', Icon: HelpCircle },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6" style={{ overflowX: 'hidden' }}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-text-primary)' }}>
          Помощь и инструкции
        </h1>
        <p style={{ color: 'var(--rv-text-muted)' }}>Руководство по использованию платформы Reversia</p>
      </div>

      {/* Tabs — scrollable on mobile */}
      <div style={{ borderBottom: '2px solid var(--rv-border)', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <nav className="-mb-0.5 flex gap-1" style={{ minWidth: 'max-content' }}>
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveCategory(id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
              style={{
                borderBottom: activeCategory === id ? '2px solid var(--rv-teal)' : '2px solid transparent',
                color: activeCategory === id ? 'var(--rv-teal)' : 'var(--rv-text-muted)',
                background: 'none',
                border: 'none',
                borderBottom: activeCategory === id ? '2px solid var(--rv-teal)' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
                marginBottom: '-2px',
              }}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </nav>
      </div>

      {/* Customer instructions */}
      {activeCategory === 'customer' && (
        <div className="reversia-card p-6">
          <h3 className="text-lg font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-text-primary)' }}>
            Инструкция для заказчиков
          </h3>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: 'var(--rv-teal)', fontFamily: 'Manrope, sans-serif' }}>
              1. Создание задания
            </h4>
            <div className="space-y-2 text-sm" style={{ color: 'var(--rv-text-primary)' }}>
              <p><strong>Шаг 1:</strong> Нажмите кнопку "Создать задание" на главной странице</p>
              <p><strong>Шаг 2:</strong> Выберите тип услуги (фото/видео, мемориальный сервис и т.д.)</p>
              <p><strong>Шаг 3:</strong> Заполните подробное описание задания:</p>
              <ul className="list-disc pl-8 space-y-1 mt-1">
                <li>Укажите точный адрес выполнения</li>
                <li>Опишите что именно нужно сфотографировать/снять</li>
                <li>Укажите желаемую дату и время</li>
                <li>При необходимости прикрепите референсные фото</li>
              </ul>
              <p><strong>Шаг 4:</strong> Установите справедливую цену (рекомендации по ценам есть в форме)</p>
              <p><strong>Шаг 5:</strong> Оплатите задание через ЮКасса</p>
            </div>
          </section>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: 'var(--rv-teal)', fontFamily: 'Manrope, sans-serif' }}>
              2. Работа с исполнителем
            </h4>
            <div className="space-y-3">
              <div className="p-4 rounded-md" style={{ background: 'rgba(13,148,136,0.04)', border: '1px solid rgba(13,148,136,0.1)' }}>
                <p className="font-medium mb-2 text-sm" style={{ color: 'var(--rv-text-primary)' }}>Обсуждение деталей:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: 'var(--rv-text-muted)' }}>
                  <li>Исполнитель может задать уточняющие вопросы в чате</li>
                  <li>Вы можете предложить изменение цены, если задание оказалось сложнее</li>
                  <li>После согласования исполнитель нажимает "Принять задание"</li>
                </ul>
              </div>

              <div className="p-4 rounded-md" style={{ background: 'rgba(217,119,6,0.04)', border: '1px solid rgba(217,119,6,0.15)' }}>
                <p className="font-medium mb-2 text-sm" style={{ color: '#92400e' }}>Изменение цены (если требуется доплата):</p>
                <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: '#b45309' }}>
                  <li>Если задание оказалось сложнее, вы можете предложить увеличение цены через чат</li>
                  <li>Исполнитель должен подтвердить новую цену</li>
                  <li><strong>ВНИМАНИЕ:</strong> После подтверждения исполнителем на странице задания появится блок "Требуется доплата"</li>
                  <li>Нажмите на кнопку "Оплатить доплату" и завершите оплату через ЮКасса</li>
                  <li>После оплаты исполнитель сможет принять задание и приступить к работе</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: 'var(--rv-teal)', fontFamily: 'Manrope, sans-serif' }}>
              3. Проверка и подтверждение результатов
            </h4>
            <div className="space-y-3 text-sm" style={{ color: 'var(--rv-text-primary)' }}>
              <div className="p-4 rounded-md" style={{ background: 'rgba(217,119,6,0.04)', borderLeft: '3px solid #d97706' }}>
                <p className="font-medium mb-1">До оплаты:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: 'var(--rv-text-muted)' }}>
                  <li>Вы увидите превью результатов с водяными знаками</li>
                  <li>Кнопка "Скачать" будет недоступна до подтверждения</li>
                  <li>Это защита качества — оцените результат перед оплатой</li>
                </ul>
              </div>

              <div className="p-4 rounded-md" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <p className="font-medium mb-2">После проверки у вас 3 варианта:</p>
                <ul className="list-disc pl-5 space-y-2 text-xs">
                  <li className="flex items-start gap-1.5">
                    <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
                    <span><strong>Подтвердить выполнение</strong> — если всё устраивает, деньги переводятся исполнителю, файлы становятся доступны</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <XCircle size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#ef4444' }} />
                    <span><strong>Открыть спор</strong> — если результат не соответствует техзаданию</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <FileText size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#d97706' }} />
                    <span><strong>Подать жалобу</strong> — если исполнитель нарушил правила платформы</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-3" style={{ color: 'var(--rv-teal)', fontFamily: 'Manrope, sans-serif' }}>
              4. Скачивание файлов результатов
            </h4>
            <div className="text-sm" style={{ color: 'var(--rv-text-primary)' }}>
              <p className="mb-2">После подтверждения выполнения задания вы можете скачать результаты:</p>
              <ul className="list-disc pl-8 space-y-1 text-sm">
                <li><strong>По отдельности:</strong> Нажмите кнопку "Скачать" под каждым файлом</li>
                <li><strong>Архивом:</strong> Нажмите "Скачать все файлы" — создастся ZIP со всеми результатами</li>
              </ul>
            </div>
          </section>
        </div>
      )}

      {/* Executor instructions */}
      {activeCategory === 'executor' && (
        <div className="reversia-card p-6">
          <h3 className="text-lg font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-text-primary)' }}>
            Инструкция для исполнителей
          </h3>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: '#10b981', fontFamily: 'Manrope, sans-serif' }}>
              1. Поиск заданий
            </h4>
            <div className="text-sm space-y-2" style={{ color: 'var(--rv-text-primary)' }}>
              <p>Доступные задания отображаются на главной странице</p>
              <p>Вы можете отфильтровать задания по типу услуги и региону</p>
            </div>
          </section>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: '#10b981', fontFamily: 'Manrope, sans-serif' }}>
              2. Принятие задания
            </h4>
            <div className="space-y-3 text-sm" style={{ color: 'var(--rv-text-primary)' }}>
              <ul className="list-disc pl-8 space-y-2">
                <li>Изучите описание задания внимательно</li>
                <li>Если что-то непонятно — задайте уточняющие вопросы в чате</li>
                <li>Если задание сложнее описания — предложите изменение цены через чат</li>
                <li>Дождитесь подтверждения новой цены заказчиком и оплаты доплаты</li>
                <li>Когда появится кнопка "Принять задание" — нажмите её, если готовы выполнить</li>
              </ul>

              <div className="p-3 rounded-md" style={{ background: 'rgba(217,119,6,0.05)', borderLeft: '3px solid #d97706' }}>
                <p className="text-xs font-medium mb-1" style={{ color: '#92400e' }}>
                  Если кнопка "Принять задание" не отображается:
                </p>
                <p className="text-xs" style={{ color: '#b45309' }}>
                  Вероятно, вы согласились на новую цену, но заказчик ещё не оплатил доплату.
                  Вы увидите информационное сообщение "Ожидаем оплату доплаты заказчиком".
                  Кнопка появится сразу после оплаты.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: '#10b981', fontFamily: 'Manrope, sans-serif' }}>
              3. Выполнение задания
            </h4>
            <div className="text-sm space-y-2" style={{ color: 'var(--rv-text-primary)' }}>
              <p>После выполнения загрузите результаты через кнопку "Завершить задание"</p>
              <div className="p-4 rounded-md" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <p className="font-medium mb-1">Для мемориальных сервисов:</p>
                <p className="text-xs" style={{ color: 'var(--rv-text-muted)' }}>
                  Отмечайте выполненные пункты в чек-листе по мере выполнения работ
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h4 className="font-semibold mb-3" style={{ color: '#10b981', fontFamily: 'Manrope, sans-serif' }}>
              4. Получение выплат
            </h4>
            <div className="text-sm space-y-2" style={{ color: 'var(--rv-text-primary)' }}>
              <p>Перейдите в раздел "Выплаты" (Способы получения выплат)</p>
              <ul className="list-disc pl-8 space-y-1">
                <li>Укажите номер телефона для СБП</li>
                <li>При необходимости добавьте банковские реквизиты счёта в разделе "Бизнес-статус"</li>
                <li>После подтверждения задания заказчиком выплата поступит автоматически или вручную администратором</li>
              </ul>
            </div>
          </section>

          <section data-testid="telegram-notifications-instruction">
            <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#10b981', fontFamily: 'Manrope, sans-serif' }}>
              <Bell size={16} />5. Уведомления о новых заданиях в Telegram
            </h4>
            <div className="text-sm space-y-2" style={{ color: 'var(--rv-text-primary)' }}>
              <p>Подключите Telegram, чтобы мгновенно получать уведомления о новых поручениях рядом с вами:</p>
              <ul className="list-disc pl-8 space-y-2 mt-2">
                <li><strong>Шаг 1:</strong> Откройте раздел "Уведомления" в меню приложения</li>
                <li><strong>Шаг 2:</strong> Нажмите кнопку "Подключить Telegram" — приложение сформирует персональную ссылку (действует 15 минут)</li>
                <li><strong>Шаг 3:</strong> Нажмите <b>"Открыть в Telegram"</b> (откроет приложение напрямую) или <b>"Открыть в браузере"</b> — откроется бот Reversia, нажмите "Запустить" (Start)</li>
                <li><strong>Шаг 4:</strong> Бот попросит отправить геолокацию — нажмите кнопку "Отправить местоположение"</li>
                <li><strong>Шаг 5:</strong> Укажите радиус уведомлений в километрах (например, 10)</li>
                <li><strong>Готово:</strong> при появлении нового поручения в вашем радиусе вы получите уведомление прямо в Telegram</li>
              </ul>

              <div className="p-4 rounded-md mt-2" style={{ background: 'rgba(13,148,136,0.05)', border: '1px solid rgba(13,148,136,0.15)' }}>
                <p className="font-medium mb-1" style={{ color: 'var(--rv-text-primary)' }}>Команды внутри бота:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: 'var(--rv-text-muted)' }}>
                  <li><strong>/status</strong> — показать, к какому аккаунту привязан Telegram и текущий радиус уведомлений</li>
                  <li><strong>/stop</strong> — отключить уведомления и отвязать Telegram от аккаунта</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* FAQ — beautiful accordion */}
      {activeCategory === 'faq' && <FAQSection />}

      {/* Support */}
      <div className="reversia-card p-6" style={{ background: 'rgba(13,148,136,0.04)', borderColor: 'rgba(13,148,136,0.15)' }}>
        <h3 className="font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--rv-text-primary)' }}>
          Не нашли ответ?
        </h3>
        <p className="text-sm mb-3" style={{ color: 'var(--rv-text-muted)' }}>Свяжитесь с нашей службой поддержки:</p>
        <div className="space-y-1 text-sm">
          <p style={{ color: 'var(--rv-text-primary)' }}>
            <strong>Email:</strong>{' '}
            <a href="mailto:support@reversia.ru" style={{ color: 'var(--rv-teal)', textDecoration: 'none' }}>
              support@reversia.ru
            </a>
          </p>
          <p style={{ color: 'var(--rv-text-muted)' }}>
            <strong style={{ color: 'var(--rv-text-primary)' }}>Режим работы:</strong> Пн–Пт, 9:00–18:00 (МСК)
          </p>
        </div>
      </div>
    </div>
  );
};
